// Sends a lead email from the signed-in user's Outlook (Microsoft Graph, delegated Mail.Send), either shown as from
// the sales address or from the user personally (the CEO writes to C-level), and always BCC'd to the sales address so
// the group sees every send. Sign-in is a PKCE popup on an Entra app registered as a
// single-page app (redirect https://crm.ithnk.com/outlook.html); the user needs Send As rights on the sales address.
// The access token lives in sessionStorage only (an hour); no refresh token is kept.
import {
  SENIOR,
  senderFor,
  type Sender,
} from "../../../../../supabase/functions/_shared/outreach.ts";

export const DEFAULT_SEND_AS = "sales@ithnk.com";

// shared with the nightly lead run (outreach function)
export { SENIOR, senderFor, type Sender };
export const SENDERS: Sender[] = ["sales", "personal"];

const SCOPE = "https://graph.microsoft.com/Mail.Send";
const TOKEN_KEY = "ithnk.outlook.token";
const MAX_ATTACHMENTS = 2_800_000; // all files together: sendMail takes 4 MB per request and base64 grows a file by a third

type Attachment = { name: string; contentType: string; base64: string };
export type OutlookMail = {
  to: string;
  subject: string;
  body: string;
  attachments?: Attachment[];
};

/** The Graph sendMail payload: from the sales address, BCC to it, saved to the user's Sent Items */
export const graphMessage = (
  mail: OutlookMail,
  sendAs: string,
  sender: Sender = "sales",
) => ({
  message: {
    subject: mail.subject,
    body: { contentType: "Text", content: mail.body },
    toRecipients: [{ emailAddress: { address: mail.to } }],
    bccRecipients: [{ emailAddress: { address: sendAs } }],
    // no "from": Graph sends as the signed-in user
    ...(sender === "sales" && { from: { emailAddress: { address: sendAs } } }),
    attachments: (mail.attachments ?? []).map((file) => ({
      "@odata.type": "#microsoft.graph.fileAttachment",
      name: file.name,
      contentType: file.contentType,
      contentBytes: file.base64,
    })),
  },
  saveToSentItems: true,
});

/** The tenant to sign in to: the sales address's domain (ithnk.com) */
export const tenantOf = (sendAs: string) =>
  sendAs.split("@")[1] || "organizations";

const base64url = (bytes: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const cachedToken = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(TOKEN_KEY) ?? "null");
    return saved && saved.expires > Date.now() + 60_000
      ? (saved.token as string)
      : null;
  } catch {
    return null;
  }
};

/**
 * A Graph token, signing in through a popup when none is cached. Call it straight from a click: the popup is opened
 * before anything is awaited, or the browser blocks it.
 */
export const outlookToken = async (clientId: string, sendAs: string) => {
  const cached = cachedToken();
  if (cached) return cached;
  const popup = window.open("", "outlook", "width=520,height=680");
  if (!popup)
    throw new Error("Allow pop-ups for the CRM to sign in to Outlook");
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = base64url(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)),
  );
  const state = base64url(crypto.getRandomValues(new Uint8Array(16)));
  const redirect = `${window.location.origin}/outlook.html`;
  const authority = `https://login.microsoftonline.com/${tenantOf(sendAs)}/oauth2/v2.0`;
  popup.location.href = `${authority}/authorize?${new URLSearchParams({
    client_id: clientId,
    response_type: "code",
    redirect_uri: redirect,
    scope: SCOPE,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    prompt: "select_account",
  })}`;
  const code = await new Promise<string>((resolve, reject) => {
    const closed = setInterval(() => {
      if (popup.closed) done(() => reject(new Error("Outlook sign-in closed")));
    }, 500);
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== popup)
        return;
      const params = new URLSearchParams(String(event.data));
      done(() =>
        params.get("state") !== state
          ? reject(new Error("Outlook sign-in did not match"))
          : params.get("code")
            ? resolve(params.get("code")!)
            : reject(
                new Error(
                  params.get("error_description") ?? "Outlook sign-in failed",
                ),
              ),
      );
    };
    const done = (finish: () => void) => {
      clearInterval(closed);
      window.removeEventListener("message", onMessage);
      popup.close();
      finish();
    };
    window.addEventListener("message", onMessage);
  });
  const response = await fetch(`${authority}/token`, {
    method: "POST",
    body: new URLSearchParams({
      client_id: clientId,
      grant_type: "authorization_code",
      code,
      redirect_uri: redirect,
      code_verifier: verifier,
      scope: SCOPE,
    }),
  });
  const answer = await response.json();
  if (!response.ok || !answer.access_token)
    throw new Error(answer.error_description ?? "Outlook sign-in failed");
  sessionStorage.setItem(
    TOKEN_KEY,
    JSON.stringify({
      token: answer.access_token,
      expires: Date.now() + answer.expires_in * 1000,
    }),
  );
  return answer.access_token as string;
};

/**
 * The brochures as files, in order, while they fit in one email together; a brochure that cannot be read here (CORS)
 * or does not fit is returned in `links`, for the email body.
 */
export const fetchAttachments = async <B extends { name: string; url: string }>(
  brochures: B[],
) => {
  const files: Attachment[] = [];
  const links: B[] = [];
  let used = 0;
  for (const brochure of brochures) {
    try {
      const response = await fetch(brochure.url);
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      if (used + blob.size > MAX_ATTACHMENTS) throw new Error();
      used += blob.size;
      const bytes = new Uint8Array(await blob.arrayBuffer());
      let binary = "";
      for (const byte of bytes) binary += String.fromCharCode(byte);
      const file = new URL(brochure.url).pathname.split("/").pop() || "";
      files.push({
        name: file.includes(".")
          ? decodeURIComponent(file)
          : `${brochure.name}.pdf`,
        contentType: blob.type || "application/pdf",
        base64: btoa(binary),
      });
    } catch {
      links.push(brochure);
    }
  }
  return { files, links };
};

export const sendOutlookMail = async (
  token: string,
  mail: OutlookMail,
  sendAs: string,
  sender: Sender = "sales",
) => {
  const response = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(graphMessage(mail, sendAs, sender)),
  });
  if (response.status === 401) sessionStorage.removeItem(TOKEN_KEY);
  if (!response.ok) {
    const answer = await response.json().catch(() => ({}));
    throw new Error(
      `Outlook answered ${response.status}: ${answer.error?.message ?? response.statusText}`,
    );
  }
};

/** The body with a line per brochure link, for brochures that go as links */
export const withLinks = (
  body: string,
  linked: { name: string; url: string }[],
) =>
  linked.length
    ? `${body}\n\n${linked.map((b) => `${b.name}: ${b.url}`).join("\n")}`
    : body;

/**
 * Sign in if needed, attach the brochures that fit (the rest as links) and send. Call it straight from a click: the
 * sign-in popup must open inside it.
 */
export const sendLeadEmail = async (input: {
  clientId: string;
  sendAs: string;
  sender: Sender;
  to: string;
  subject: string;
  body: string;
  brochures: { name: string; url: string }[];
}) => {
  const token = await outlookToken(input.clientId, input.sendAs);
  const { files, links } = await fetchAttachments(input.brochures);
  await sendOutlookMail(
    token,
    {
      to: input.to.trim(),
      subject: input.subject,
      body: withLinks(input.body, links),
      attachments: files,
    },
    input.sendAs,
    input.sender,
  );
  return {
    attached: files.map((f) => f.name),
    linked: links.map((l) => l.name),
  };
};
